
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Edit3, 
  Copy, 
  Save, 
  X, 
  Maximize2, 
  Minimize2,
  Type,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

interface Template {
  id: string;
  title: string;
  description: string;
  content: string;
  category: string;
  type: 'saved' | 'bookmark' | 'popular' | 'product' | 'collection';
  custom_title?: string;
  notes?: string;
}

interface TemplateCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  template: Template | null;
  onCopyToChat: (content: string) => void;
  onSaveTemplate?: (updatedTemplate: Template) => void;
}

const TemplateCanvas: React.FC<TemplateCanvasProps> = ({
  isOpen,
  onClose,
  template,
  onCopyToChat,
  onSaveTemplate
}) => {
  const [editedContent, setEditedContent] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [hasShownToast, setHasShownToast] = useState(false);

  useEffect(() => {
    if (template) {
      setEditedContent(template.content);
      setWordCount(template.content.split(/\s+/).filter(word => word.length > 0).length);
      setCharCount(template.content.length);
    }
  }, [template]);

  const handleContentChange = (value: string) => {
    setEditedContent(value);
    setWordCount(value.split(/\s+/).filter(word => word.length > 0).length);
    setCharCount(value.length);
  };

  const handleCopyToChat = () => {
    onCopyToChat(editedContent);
    
    // Show success toast only once
    if (!hasShownToast) {
      toast.success('Template copied to chat input!');
      setHasShownToast(true);
      setTimeout(() => setHasShownToast(false), 2000);
    }
    
    onClose();
  };

  const handleSave = () => {
    if (template && onSaveTemplate) {
      const updatedTemplate = {
        ...template,
        content: editedContent
      };
      onSaveTemplate(updatedTemplate);
      
      // Show success toast only once
      if (!hasShownToast) {
        toast.success('Template saved successfully!');
        setHasShownToast(true);
        setTimeout(() => setHasShownToast(false), 2000);
      }
    }
  };

  if (!template) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`${isFullscreen ? 'max-w-[100vw] h-[100vh] w-full' : 'max-w-7xl h-[90vh] w-[95vw]'} flex flex-col p-0`}>
        <DialogHeader className="flex-shrink-0 p-6 pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-3 text-xl">
                <FileText className="h-5 w-5 text-purple-600" />
                {template.custom_title || template.title}
              </DialogTitle>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-xs">
                  {template.category}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {template.type}
                </Badge>
              </div>
              {template.description && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  {template.description}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="h-8 w-8 p-0"
              >
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0 p-6">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <Type className="h-3 w-3" />
                {wordCount} words, {charCount} characters
              </span>
            </div>
            <div className="text-xs text-gray-400">
              Use Markdown formatting for better structure
            </div>
          </div>
          <Textarea
            value={editedContent}
            onChange={(e) => handleContentChange(e.target.value)}
            className="flex-1 min-h-[500px] font-mono text-sm resize-none"
            placeholder="Edit your template content here... You can use Markdown formatting."
          />
        </div>

        <div className="flex-shrink-0 p-6 pt-4 border-t bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500">
              Changes are automatically saved as you type
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {onSaveTemplate && (
                <Button variant="outline" onClick={handleSave} className="flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  Save Template
                </Button>
              )}
              <Button onClick={handleCopyToChat} className="flex items-center gap-2">
                <Copy className="h-4 w-4" />
                Copy to Chat
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateCanvas;
